import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import Header from '../Common/Header/Header';
import { WelletContext } from '../../context/WelletContext';
import { useParams } from 'react-router-dom';
import ContentApp from '../ContentApp';
import { getLocale, isTodayFormat, validateDateAmPm } from '../../helpers/dates';
import { useSelector } from 'react-redux';
import { Spinner } from '../Common/Spinner/Spinner';
import EmptyActivity from '../Common/EmptyActivity/EmptyActivity';
import SearchIcon from '../icons/SearchIcon';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';

const ListQrCheckin = () => {
  const [infoCheckinTickets, setInfoCheckinTickets] = useState([]);
  const [loading, setLoading] = useState(false); 
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false); 

  const welletContext = useContext(WelletContext);
  const { id } = useParams();
  const app = useSelector((state) => state.app);
  const locale = getLocale(app.language);
  const { t } = useTranslation();

  const pageRef = useRef(0);
  const searchValueRef = useRef('');
  const fetchingRef = useRef(false);
  const checkInTickets = useCallback(async () => {
    if (fetchingRef.current || !hasMore) return;

    fetchingRef.current = true;
    setLoadingMore(true);
    try {
      const response = await welletContext.apis.tickets.post(
        `/api/checkIn/checkInRegistered?eventShowId=${id}`,
        {
          limit: 10,
          skip: pageRef.current * 10,
          filterField: searchValueRef.current,
        }
      );

      const newTickets = response.data || [];
      setInfoCheckinTickets((prevTickets) => [
        ...prevTickets,
        ...newTickets
      ]);

      setNoResults(newTickets.length === 0 && searchValueRef.current !== '');

      if (newTickets.length < 10) {
        setHasMore(false);
      } else {
        pageRef.current += 1;
      }
    } catch (error) {
      console.error(error);
      setError('Error al cargar los tickets');
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
      setSearching(false);
    }
  }, [id, welletContext.apis.tickets, hasMore]);

  const handleScroll = useCallback(() => {
    if (window.innerHeight + document.documentElement.scrollTop + 1 >= document.documentElement.scrollHeight) {
      checkInTickets();
    }
  }, [checkInTickets]);

  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const delayedSearch = useRef(
    debounce((query) => {
      setSearching(true);
      searchValueRef.current = query;
      pageRef.current = 0;
      setInfoCheckinTickets([]);
      setHasMore(true);
      checkInTickets();
    }, 500)
  ).current;

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    delayedSearch(value.length >= 3 ? value : '');
    setNoResults(false);
  };

  useEffect(() => {
    setLoading(true);
    checkInTickets();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll, checkInTickets]);

  return (
    <div>
      <Header title={t('yourCheckIn')} hasBackground={true} />
      <div className='card flex align-items-center flex-row p-2 ml-3 mr-3 filter-buttons'>
        <div className='mr-2'>
          <SearchIcon size='20' fillColor='#777777' />
        </div>
        <input
          type='text'
          placeholder={t('search')}
          className='input-search fw-semibold'
          onChange={handleSearchInputChange}
        />
      </div>
      <ContentApp menu>
        {error ? (
          <p>{error}</p>
        ) : infoCheckinTickets.length > 0 ? (
          infoCheckinTickets.map((ticket, index) => (
            <div key={index} className='card p-3 mt-3'>
              <div className='d-flex justify-content-between align-items-center'>
                <div className='d-flex flex-column'>
                  <span style={{ fontSize: '20px' }}>{ticket.customerName}</span>
                  <div className='text-secondary' style={{ fontSize: '16px' }}>
                    {ticket.code}
                  </div>
                </div>
                <div className='d-flex flex-column align-items-end'>
                  <span style={{ fontSize: '16px' }}>

                    <div>{format(parseISO(ticket.checkInDate), 'h:mm aaa', { locale })}</div>
                  </span>
                  <div className='text-secondary' style={{ fontSize: '16px' }}>
                  <div>{isTodayFormat(ticket.checkInDate, locale, t)}</div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) :  noResults && !loadingMore ? (
          <div className='mt-3'>
            <EmptyActivity
              title={t("noMatchesFound")}
              subtitle={t("tryAnotherSearchTerm")}
            />
          </div>
        ) : infoCheckinTickets && !loadingMore ?(
          <div className='mt-3'>
            <EmptyActivity
              title={t("weAreSorry")}
              subtitle={t("thereAreNoScannedTicketsThisTime")}
            />
          </div>
        ) : null}
        
        {loadingMore && (
          <div className='d-flex justify-content-center align-items-center' style={{ height: '80vh' }}>
          <Spinner />
        </div>
        )}
      </ContentApp>
    </div>
  );
};

export default ListQrCheckin;
